import axios from 'axios';
import { Bet, Race, RaceStatus, TokenStats, UserRaces } from '../types/race';
import useCoingeckoStore from '../stores/coingeckoStore';
import { formatUnits, maxUint256 } from 'viem';
import dayjs from 'dayjs';
import config from '../config/config';

interface TokenStatsResponse {
  id: string;
  token: {
    id: string;
  };
  totalBetsPlaced: string;
  totalBetsAmount: string;
  startFdv: string;
  startPrice: string;
  startVolatility: string;
  endFdv: string;
  endPrice: string;
  endVolatility: string;
}

interface RaceResponse {
  id: string;
  name: string;
  creator: {
    id: string;
  };
  bettingStartTime: string;
  raceStartTime: string;
  raceEndTime: string;
  creatorFeePercent: string;
  platformFeePercent: string;
  handicapEnabled: boolean;
  totalPool: string;
  concluded: boolean;
  tokenStats: TokenStatsResponse[];
  winnerStats?: TokenStatsResponse;
  bets: Bet[];
  totalWinningsClaimed: string;
  totalParticipants: string;
  createdAt: string;
  oracleStartTimestamp: string;
  oracleEndTimestamp: string;
}

interface UserResponse {
  id: string;
  createdRaces: RaceResponse[];
  bets: {
    race: RaceResponse;
  }[];
}

interface GraphQLRaceResponse {
  data: { race: RaceResponse };
}

interface GraphQLRacesResponse {
  data: {
    races: RaceResponse[];
  };
}

interface GraphQLUserResponse {
  data: { user: UserResponse };
}

const transformTokensStats = async (
  tokenStats: TokenStatsResponse[],
  raceStartTime: bigint,
  raceEndTime: bigint,
  handicapEnabled: boolean
): Promise<TokenStats[]> => {
  const coingeckoStore = useCoingeckoStore.getState();

  // Fetch all market data in one call using the store
  const marketData = await coingeckoStore.getCoins(tokenStats.map(stats => stats.token.id));

  let minStartFDV = maxUint256;
  let minEndFDV = maxUint256;
  if (handicapEnabled) {
    tokenStats.forEach(stats => {
      if (BigInt(stats.startFdv) < minStartFDV) {
        minStartFDV = BigInt(stats.startFdv);
      }
      if (BigInt(stats.endFdv) < minEndFDV) {
        minEndFDV = BigInt(stats.endFdv);
      }
    });
  }

  // Transform tokenStats with market data
  return tokenStats.map(stats => {
    const coinData = marketData[stats.token.id.toLowerCase()];
    const now = BigInt(dayjs().unix());
    let score = 0;
    let lastRecordedPrice = 0;
    if (now < raceStartTime) {
      score = 0;
      lastRecordedPrice = coinData?.current_price || 0;
    } else if (now >= raceEndTime) {
      if (handicapEnabled) {
        const priceChange =
          (Number(formatUnits(BigInt(stats.endPrice), 18)) - Number(formatUnits(BigInt(stats.startPrice), 18))) /
          Number(formatUnits(BigInt(stats.startPrice), 18));
        const volatilityFactor = priceChange / Number(formatUnits(BigInt(stats.endVolatility), 18));
        const fdvRatio = Number(formatUnits(BigInt(stats.endFdv), 18)) / Number(formatUnits(minEndFDV, 18)) + 1;
        score = volatilityFactor * Math.log(fdvRatio) * 100;
      } else {
        score =
          ((Number(formatUnits(BigInt(stats.endPrice), 18)) - Number(formatUnits(BigInt(stats.startPrice), 18))) /
            Number(formatUnits(BigInt(stats.startPrice), 18))) *
          100;
      }

      lastRecordedPrice = Number(formatUnits(BigInt(stats.endPrice), 18));
    } else {
      if (handicapEnabled) {
        const priceChange =
          (Number(coinData?.current_price || 0) - Number(formatUnits(BigInt(stats.startPrice), 18))) /
          Number(formatUnits(BigInt(stats.startPrice), 18));
        const volatilityFactor = priceChange / Number(formatUnits(BigInt(stats.startVolatility), 18));
        const fdvRatio = Number(formatUnits(BigInt(stats.startFdv), 18)) / Number(formatUnits(minStartFDV, 18)) + 1;
        score = volatilityFactor * Math.log(fdvRatio) * 100;
      } else {
        score =
          ((Number(coinData?.current_price || 0) - Number(formatUnits(BigInt(stats.startPrice), 18))) /
            Number(formatUnits(BigInt(stats.startPrice), 18))) *
          100;
      }

      lastRecordedPrice = coinData?.current_price || 0;
    }

    // !Remove once CoinGecko API is fixed
    if (isNaN(score) || !isFinite(score)) {
      score = coinData?.price_change_percentage_24h || 0;
      lastRecordedPrice = coinData?.current_price || 0;
    }

    return {
      ...stats,
      totalBetsPlaced: BigInt(stats.totalBetsPlaced),
      totalBetsAmount: BigInt(stats.totalBetsAmount),
      startFdv: BigInt(stats.startFdv),
      startPrice: BigInt(stats.startPrice),
      startVolatility: BigInt(stats.startVolatility),
      endFdv: BigInt(stats.endFdv),
      endPrice: BigInt(stats.endPrice),
      endVolatility: BigInt(stats.endVolatility),
      token: {
        ...stats.token,
        name: coinData?.name || stats.token.id,
        symbol: coinData?.symbol.toUpperCase() || stats.token.id,
        marketData: coinData,
        score,
        lastRecordedPrice,
      },
    };
  });
};

const transformRace = async (race: RaceResponse): Promise<Race> => {
  const now = BigInt(dayjs().unix());
  const bettingStartTime = BigInt(race.bettingStartTime);
  const raceStartTime = BigInt(race.raceStartTime);
  const raceEndTime = BigInt(race.raceEndTime);

  let status: RaceStatus;
  if (race.concluded || now >= Number(race.raceEndTime)) {
    status = RaceStatus.CONCLUDED;
  } else if (now >= bettingStartTime && now < raceStartTime) {
    status = RaceStatus.BETTING;
  } else {
    status = RaceStatus.IN_PROGRESS;
  }

  const tokenStats = await transformTokensStats(race.tokenStats, raceStartTime, raceEndTime, race.handicapEnabled);
  const winnerStats = tokenStats.find(tokenStat => tokenStat.token.id === race.winnerStats?.token.id);
  const bets = race.bets.map(bet => ({
    ...bet,
    amount: BigInt(bet.amount),
    timestamp: BigInt(bet.timestamp),
  }));

  return {
    ...race,
    bets,
    bettingStartTime,
    raceStartTime,
    raceEndTime: BigInt(race.raceEndTime),
    creatorFeePercent: BigInt(race.creatorFeePercent),
    platformFeePercent: BigInt(race.platformFeePercent),
    totalPool: BigInt(race.totalPool),
    totalWinningsClaimed: BigInt(race.totalWinningsClaimed),
    totalParticipants: BigInt(race.totalParticipants),
    createdAt: BigInt(race.createdAt),
    tokenStats,
    winnerStats,
    status,
    oracleStartTimestamp: BigInt(race.oracleStartTimestamp),
    oracleEndTimestamp: BigInt(race.oracleEndTimestamp),
  };
};

const transformUser = async (user: UserResponse): Promise<UserRaces> => {
  const bets = await Promise.all(
    Object.values(
      user.bets.reduce(
        (acc, bet) => {
          if (!acc[bet.race.id]) {
            acc[bet.race.id] = bet.race;
          }
          return acc;
        },
        {} as Record<string, RaceResponse>
      )
    ).map(transformRace)
  );

  return {
    ...user,
    createdRaces: await Promise.all(user.createdRaces.map(transformRace)),
    bets,
  };
};

export const GET_ACTIVE_RACES = `
  query GetActiveRaces {
    races(where: { raceStartTime_gt: ${dayjs().unix()} }, orderBy: createdAt, orderDirection: desc) {
      id
      name
      creator {
        id
      }
      bettingStartTime
      raceStartTime
      raceEndTime
      creatorFeePercent
      platformFeePercent
      handicapEnabled
      totalPool
      concluded
      tokenStats {
        id
        token {
          id
        }
        totalBetsPlaced
        totalBetsAmount
        startFdv
        startPrice
        startVolatility
        endFdv
        endPrice
        endVolatility
      }
      winnerStats {
        id
        token {
          id
        }
        totalBetsPlaced
        totalBetsAmount
        startFdv
        startPrice
        startVolatility
        endFdv
        endPrice
        endVolatility
      }
      bets {
        bettor {
          id
        }
        token {
          id
        }
        amount
        timestamp
      }
      totalWinningsClaimed
      totalParticipants
      createdAt
      oracleStartTimestamp
      oracleEndTimestamp
    }
  }
`;

export const GET_RACE_BY_ID = `
  query GetRaceById($id: ID!) {
    race(id: $id) {
      id
      name
      creator {
        id
      }
      bettingStartTime
      raceStartTime
      raceEndTime
      creatorFeePercent
      platformFeePercent
      handicapEnabled
      totalPool
      concluded
      tokenStats {
        id
        token {
          id
        }
        totalBetsPlaced
        totalBetsAmount
        startFdv
        startPrice
        startVolatility
        endFdv
        endPrice
        endVolatility
      }
      winnerStats {
        id
        token {
          id
        }
        totalBetsPlaced
        totalBetsAmount
        startFdv
        startPrice
        startVolatility
        endFdv
        endPrice
        endVolatility
      }
      bets {
        bettor {
          id
        }
        token {
          id
        }
        amount
        timestamp
      }
      totalWinningsClaimed
      totalParticipants
      createdAt
      oracleStartTimestamp
      oracleEndTimestamp
    }
  }
`;

export const GET_ONGOING_RACES = `
  query GetOngoingRaces {
    races(where: { raceStartTime_lte: ${dayjs().unix()}, raceEndTime_gte: ${dayjs().unix()} }, orderBy: createdAt, orderDirection: desc) {
      id
      name
      creator {
        id
      }
      bettingStartTime
      raceStartTime
      raceEndTime
      creatorFeePercent
      platformFeePercent
      handicapEnabled
      totalPool
      concluded
      tokenStats {
        id
        token {
          id
        }
        totalBetsPlaced
        totalBetsAmount
        startFdv
        startPrice
        startVolatility
        endFdv
        endPrice
        endVolatility
      }
      bets {
        bettor {
          id
        }
        token {
          id
        }
        amount
        timestamp
      }
      totalWinningsClaimed
      totalParticipants
      createdAt
      oracleStartTimestamp
      oracleEndTimestamp
    }
  }
`;

export const GET_USER_RACES = `
  query GetUserRaces($address: String!) {
    user(id: $address) {
      id
      createdRaces(orderBy: createdAt, orderDirection: desc) {
        id
        name
        creator {
          id
        }
        bettingStartTime
        raceStartTime
        raceEndTime
        creatorFeePercent
        platformFeePercent
        handicapEnabled
        totalPool
        concluded
        tokenStats {
          id
          token {
            id
          }
          totalBetsPlaced
          totalBetsAmount
          startFdv
          startPrice
          startVolatility
          endFdv
          endPrice
          endVolatility
        }
        bets {
          bettor {
            id
          }
          token {
            id
          }
          amount
          timestamp
        }
        totalWinningsClaimed
        totalParticipants
        createdAt
        oracleStartTimestamp
        oracleEndTimestamp
      }
      bets {
        race {
          id
          name
          creator {
            id
          }
          bettingStartTime
          raceStartTime
          raceEndTime
          creatorFeePercent
          platformFeePercent
          handicapEnabled
          totalPool
          concluded
          tokenStats {
            id
            token {
              id
            }
            totalBetsPlaced
            totalBetsAmount
            startFdv
            startPrice
            startVolatility
            endFdv
            endPrice
            endVolatility
          }
          bets {
            bettor {
              id
            }
            token {
              id
            }
            amount
            timestamp
          }
          totalWinningsClaimed
          totalParticipants
          createdAt
          oracleStartTimestamp
          oracleEndTimestamp
        }
      }
    }
  }
`;

export const fetchActiveRaces = async (): Promise<Race[]> => {
  const response = await axios.post<GraphQLRacesResponse>(config.get('subgraph.url'), {
    query: GET_ACTIVE_RACES,
  });
  return Promise.all(response.data.data.races.map(transformRace));
};

export const fetchRaceById = async (id: string): Promise<Race> => {
  const response = await axios.post<GraphQLRaceResponse>(config.get('subgraph.url'), {
    query: GET_RACE_BY_ID,
    variables: { id },
  });
  return transformRace(response.data.data.race);
};

export const fetchOngoingRaces = async (): Promise<Race[]> => {
  const response = await axios.post<GraphQLRacesResponse>(config.get('subgraph.url'), {
    query: GET_ONGOING_RACES,
  });
  return Promise.all(response.data.data.races.map(transformRace));
};

export const fetchUserRaces = async (address: string): Promise<UserRaces | null> => {
  const response = await axios.post<GraphQLUserResponse>(config.get('subgraph.url'), {
    query: GET_USER_RACES,
    variables: { address: address.toLowerCase() },
  });
  if (!response.data.data.user) {
    return null;
  }
  return transformUser(response.data.data.user);
};
